/**
 * NCO routing_preference — NCO-ROUTE-PREF-001 (ref 8b504df / 492799e).
 * Order-level wire field; seeds every line's estimationMode in the DD adapter.
 */

import type { OriginRoutingPreference } from '@/types/origin'

export type { OriginRoutingPreference }

export const NCO_ROUTING_OPTIONS: ReadonlyArray<{
  value: OriginRoutingPreference
  label: string
  sub: string
}> = [
  {
    value: 'parallel',
    label: 'Parallel Process',
    sub: 'Design + Estimation at the same time',
  },
  {
    value: 'design_first',
    label: 'Design First',
    sub: 'Concept creation before estimation',
  },
  {
    value: 'estimation_first',
    label: 'Estimation First',
    sub: 'Build must be determined first',
  },
] as const

export const NCO_ROUTING_DEFAULT: OriginRoutingPreference = 'parallel'

export function normalizeRoutingPreference(
  value: string | null | undefined
): OriginRoutingPreference {
  if (value === 'design_first' || value === 'estimation_first' || value === 'parallel') {
    return value
  }
  return NCO_ROUTING_DEFAULT
}

/** Display label for Submitted detail / HubSpot (ref index.html + hubspot-ticket-renderer). */
export function formatRoutingPreferenceLabel(
  value: string | null | undefined
): string {
  const pref = normalizeRoutingPreference(value)
  if (pref === 'design_first') return 'Design First'
  if (pref === 'estimation_first') return 'Estimation First'
  return 'Parallel Process'
}

/**
 * Order routing → line estimationMode (492799e routingToEstimationMode).
 * parallel / absent → standard (design + estimation together).
 */
export function routingToEstimationMode(
  pref: OriginRoutingPreference | null | undefined
): 'standard' | 'design_first' | 'estimation_first' {
  if (pref === 'design_first') return 'design_first'
  if (pref === 'estimation_first') return 'estimation_first'
  return 'standard'
}

/**
 * DD-DESCRIBE-001b — NCO SB confirm routing nudge (Origin ~L4147–4153).
 * Only overrides the untouched default (`parallel`); never overwrites an
 * explicit design_first / estimation_first choice. Re-runs on every confirm
 * (add or edit); no reverse when describe lines are removed.
 */
export function nudgeRoutingPreferenceForDescribe(
  preference: OriginRoutingPreference,
  buildMode: string | null | undefined
): OriginRoutingPreference {
  if (buildMode === 'describe' && preference === 'parallel') {
    return 'estimation_first'
  }
  return preference
}
